import React, { FC, useEffect, useState, useRef, useCallback } from 'react';

import { SecondIFrameContainer, useSecondIFrameDispatch, resetSecondIframeState } from 'rcx_crm_ui';

import ErrorPage from './ErrorPage';
import LoadingPage from './LoadingPage';
import { StyledGrid } from './RCX.styled';
import { RCXIFrame } from './RCXIFrame';
import { RCXContainerProps } from './types';
import { ServiceNowAdapterEvent } from '../enums';
import { gotoSNAuthorizePage } from '../utils';
import { getParameterByName } from '../utils/url';

export const RCXContainer: FC<RCXContainerProps> = ({ snAdapter, secondIframeAdapter }) => {
    const [isShowRcxIframe, setIsShowRcxIframe] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isLoadSecondIframe, setIsLoadSecondIframe] = useState(false);
    const secondIframeDispatch = useSecondIFrameDispatch();
    const [agentUIUrl, setAgentUIUrl] = useState<string>('');

    const rcxIframe = useRef<HTMLIFrameElement>(null);

    const hideIframe = useCallback(() => {
        setIsShowRcxIframe(false);
    }, []);

    const updateIsLoading = useCallback((status: boolean) => {
        setIsLoading(status);
    }, []);

    const updateIsError = useCallback((status: boolean) => {
        setIsError(status);
    }, []);

    const updateSecondIframeStatus = (status: boolean) => {
        setIsLoadSecondIframe(status);
    };

    const resetSecondIframe = () => {
        secondIframeDispatch(resetSecondIframeState());
    };

    useEffect(() => {
        snAdapter.on(ServiceNowAdapterEvent.HIDE_RCX_IFRAME, hideIframe);
        snAdapter.on(ServiceNowAdapterEvent.UPDATE_IS_LOADING, updateIsLoading);
        snAdapter.on(ServiceNowAdapterEvent.UPDATE_IS_ERROR, updateIsError);
        snAdapter.on(ServiceNowAdapterEvent.UPDATE_IS_ERROR, updateIsError);
        snAdapter.on(ServiceNowAdapterEvent.UPDATE_SECOND_IFRAME_STATUS, updateSecondIframeStatus);
        snAdapter.on(ServiceNowAdapterEvent.RESET_SECOND_IFRAME, resetSecondIframe);
        return () => {
            snAdapter.off(ServiceNowAdapterEvent.HIDE_RCX_IFRAME, hideIframe);
            snAdapter.off(ServiceNowAdapterEvent.UPDATE_IS_LOADING, updateIsLoading);
            snAdapter.off(ServiceNowAdapterEvent.UPDATE_IS_ERROR, updateIsError);
            snAdapter.off(ServiceNowAdapterEvent.UPDATE_SECOND_IFRAME_STATUS, updateSecondIframeStatus);
            snAdapter.off(ServiceNowAdapterEvent.RESET_SECOND_IFRAME, resetSecondIframe);
        };
    }, []);

    useEffect(() => {
        if (rcxIframe.current) {
            snAdapter.emit(ServiceNowAdapterEvent.RINGCX_FRAME_LOAD, rcxIframe.current);
        }
    }, []);

    useEffect(() => {
        const _url = window.location.href;
        const code = getParameterByName(_url, 'code');
        if (code) {
            snAdapter.emit(ServiceNowAdapterEvent.UPDATE_IS_LOADING, true);
        }
    }, []);

    useEffect(() => {
        const agentUIUrl = localStorage.getItem('agentUIUrl');
        setAgentUIUrl(agentUIUrl || '');
    }, []);

    const handleSignOutClick = useCallback(() => {
        snAdapter.signOutAgent();
        setTimeout(() => {
            snAdapter.emit(ServiceNowAdapterEvent.UPDATE_IS_ERROR, false);
        }, 4000);
    }, []);

    const handleTryAgainClick = useCallback(() => {
        localStorage.setItem('agentUIUrl', agentUIUrl);
        gotoSNAuthorizePage(snAdapter.configuration.domain, snAdapter.configuration.clientId);
    }, [agentUIUrl]);

    return isShowRcxIframe ? (
        <StyledGrid container justifyContent='space-between'>
            <RCXIFrame ref={rcxIframe} agentUIUrl={agentUIUrl} />
            {isLoadSecondIframe && secondIframeAdapter && (
                <SecondIFrameContainer secondIframeAdapter={secondIframeAdapter} />
            )}
            {isLoading && <LoadingPage />}
            {isError && <ErrorPage handleSignOutClick={handleSignOutClick} handleTryAgainClick={handleTryAgainClick} />}
        </StyledGrid>
    ) : null;
};
